const HOST_URL_MAX_LENGTH = 2000

const ATTRIBUTION_SENSITIVE_QUERY_KEYS: readonly string[] = [
  'token',
  'password',
  'pass',
  'passwd',
  'email',
  'mail',
  'e-mail',
  'cpf',
  'cnpj',
  'access_token',
  'access-token',
  'accesstoken',
  'jwt',
  'authorization',
  'auth',
  'session',
  'code',
  'refresh_token',
  'refresh-token',
  'api_key',
  'api-key',
  'apikey',
  'secret',
  'phone',
  'telefone',
  'document',
  'otp',
  'pin',
  'r',
  'resume_token',
]

export function isSensitiveAttributionQueryKey(key: string): boolean {
  return ATTRIBUTION_SENSITIVE_QUERY_KEYS.includes(key.trim().toLowerCase())
}

function deleteSensitiveSearchParams(searchParams: URLSearchParams): void {
  const keysToDelete: string[] = []
  searchParams.forEach((_value, key) => {
    if (isSensitiveAttributionQueryKey(key)) {
      keysToDelete.push(key)
    }
  })
  for (const key of keysToDelete) {
    searchParams.delete(key)
  }
}

export function stripSensitiveQueryFromUrl(value: string): string {
  try {
    const parsed = new URL(value.trim())
    if (parsed.username || parsed.password) {
      return ''
    }
    if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
      return ''
    }
    parsed.hash = ''
    deleteSensitiveSearchParams(parsed.searchParams)
    return parsed.toString()
  } catch {
    return value.trim()
  }
}

const ATTRIBUTION_INTERNAL_QUERY_KEYS: readonly string[] = [
  'lp_host_url',
  'lp_tracking_session',
  'embed',
]

export function isIgnoredAttributionQueryKey(key: string): boolean {
  const normalized = key.trim().toLowerCase()
  return (
    isSensitiveAttributionQueryKey(normalized) ||
    ATTRIBUTION_INTERNAL_QUERY_KEYS.includes(normalized)
  )
}

const TRACKING_SESSION_ID_PATTERN = /^[A-Za-z0-9_-]{8,64}$/

export function readLpTrackingSessionFromSearch(search: string): string | null {
  const normalized = search.startsWith('?') ? search.slice(1) : search
  const raw = new URLSearchParams(normalized).get('lp_tracking_session')
  if (!raw) {
    return null
  }
  const trimmed = raw.trim()
  return TRACKING_SESSION_ID_PATTERN.test(trimmed) ? trimmed : null
}

function capAttributionUrl(value: string): string {
  return value.slice(0, HOST_URL_MAX_LENGTH)
}

function decodePercentEncoding(value: string): string {
  let current = value
  for (let attempt = 0; attempt < 3; attempt += 1) {
    if (!/%[0-9A-Fa-f]{2}/.test(current)) {
      return current
    }
    try {
      const decoded = decodeURIComponent(current)
      if (decoded === current) {
        return current
      }
      current = decoded
    } catch {
      return current
    }
  }
  return current
}

export function formatAttributionPageLabel(value: string | null | undefined): string {
  const trimmed = (value ?? '').trim()
  if (trimmed === '') {
    return ''
  }
  return decodePercentEncoding(trimmed)
}

export function isSafeHttpUrl(value: string): boolean {
  try {
    const parsed = new URL(value.trim())
    if (parsed.username || parsed.password) {
      return false
    }
    return parsed.protocol === 'http:' || parsed.protocol === 'https:'
  } catch {
    return false
  }
}

export function httpsHrefFromUrl(value: string | null | undefined): string | null {
  if (!value) {
    return null
  }
  const stripped = stripSensitiveQueryFromUrl(value)
  if (!stripped || !isSafeHttpUrl(stripped)) {
    return null
  }
  try {
    const parsed = new URL(stripped)
    if (parsed.protocol !== 'https:') {
      return null
    }
    return parsed.toString()
  } catch {
    return null
  }
}

export function readLpHostUrlFromSearch(search: string): string | null {
  const normalized = search.startsWith('?') ? search.slice(1) : search
  const raw = new URLSearchParams(normalized).get('lp_host_url')
  if (!raw) {
    return null
  }
  const trimmed = capAttributionUrl(raw.trim())
  if (!isSafeHttpUrl(trimmed)) {
    return null
  }
  const stripped = capAttributionUrl(stripSensitiveQueryFromUrl(trimmed))
  return stripped && isSafeHttpUrl(stripped) ? stripped : null
}

export function resolveAttributionPageUrl(scriptPageUrl?: string | null): string {
  if (typeof window === 'undefined') {
    return ''
  }

  const fromScript = (scriptPageUrl ?? '').trim()
  if (fromScript && isSafeHttpUrl(fromScript)) {
    return capAttributionUrl(stripSensitiveQueryFromUrl(fromScript) || fromScript)
  }

  const fromQuery = readLpHostUrlFromSearch(window.location.search)
  if (fromQuery) {
    return fromQuery
  }

  const currentHref = window.location.href
  return capAttributionUrl(stripSensitiveQueryFromUrl(currentHref) || currentHref)
}

export type AttributionUrlSearchFields = {
  queryParams: Record<string, string>
  utm: {
    source?: string
    medium?: string
    campaign?: string
    content?: string
    term?: string
    id?: string
  }
  clickIds: Record<string, string>
}

export function collectAttributionSearchFields(pageUrl: string): AttributionUrlSearchFields {
  const queryParams: Record<string, string> = {}
  const utm: AttributionUrlSearchFields['utm'] = {}
  const clickIds: Record<string, string> = {}

  let searchParams: URLSearchParams
  try {
    searchParams = new URL(pageUrl).searchParams
  } catch {
    searchParams = typeof window === 'undefined' ? new URLSearchParams() : new URLSearchParams(window.location.search)
  }

  for (const [key, value] of searchParams.entries()) {
    if (isIgnoredAttributionQueryKey(key)) {
      continue
    }
    queryParams[key] = value
  }

  const mapUtm = (sourceKey: string, targetKey: keyof AttributionUrlSearchFields['utm']) => {
    const value = searchParams.get(sourceKey)
    if (value && value.trim() !== '') {
      utm[targetKey] = value.trim()
    }
  }

  mapUtm('utm_source', 'source')
  mapUtm('utm_medium', 'medium')
  mapUtm('utm_campaign', 'campaign')
  mapUtm('utm_content', 'content')
  mapUtm('utm_term', 'term')
  mapUtm('utm_id', 'id')

  for (const key of ['fbclid', 'gclid', 'ttclid', 'msclkid']) {
    const value = searchParams.get(key)
    if (value && value.trim() !== '') {
      clickIds[key] = value.trim()
    }
  }

  return { queryParams, utm, clickIds }
}
